import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import {
    Field,
    Form,
    FormElement,
    FormFieldSet,
    type FormRenderProps
} from "@progress/kendo-react-form";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { arrowRightIcon } from "@progress/kendo-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { useLoginMutation } from "../../api/authApi";
import { useAppDispatch } from "../../app/hooks";
import logoUrl from "../../assets/logo-300.png";
import { emailValidator, FormInputField, passwordValidator } from "../../components/kendo-form-controls";
import { login } from "../../slices/userSlice";

type LoginFormValues = {
    email?: string;
    password?: string;
};

const Login: React.FC = () => {
    const [loginMutation, { isLoading, isError, isSuccess }] = useLoginMutation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleLogin = async (values: LoginFormValues) => {
        const email = values.email ?? "";
        const password = values.password ?? "";

        const result = await loginMutation({ email, password });
        if (!result.data) {
            return;
        }

        dispatch(login({
            email: result.data?.info.email,
            firstName: result.data.info.first_name,
            lastName: result.data.info.last_name,
            roles: result.data?.info.roles,
            token: result.data?.token,
            tokenExpiry: result.data?.expires_utc
        }));

        navigate("/app");
    };

    return (
        <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-body-tertiary px-3 py-4">
            <div className="text-center mb-4">
                <img
                    src={logoUrl}
                    alt="Sovra"
                    className="mx-auto d-block mb-2"
                    style={{
                        height: "3rem",
                        width: "auto",
                        objectFit: "contain"
                    }}
                />
                <div className="text-body-secondary">
                    Commerce Decision Intelligence
                </div>
            </div>

            <Card
                className="w-100 rounded-4 border border-secondary-subtle shadow-sm"
                style={{ maxWidth: "400px" }}
            >
                <CardBody className="px-4 py-5 p-sm-5">
                    <div className="mb-4">
                        <h1 className="h3 fw-bold mb-2 text-body">Welcome back</h1>
                        <p className="mb-0 text-body-secondary">Sign in to your Sovra account.</p>
                    </div>
                    <Form
                        onSubmit={handleLogin}
                        render={(formRenderProps: FormRenderProps) => (
                            <FormElement>
                                <FormFieldSet className="mb-0">
                                    <Field
                                        id="email"
                                        name="email"
                                        label="Work Email"
                                        autoComplete="username"
                                        component={FormInputField}
                                        validator={emailValidator}
                                    />
                                    <div className="d-flex align-items-center justify-content-between gap-3 mt-3 mb-1">
                                        <span className="small text-body-secondary">Password</span>
                                        <Link className="small link-success link-underline-opacity-0" to="/forgot-password">Forgot password?</Link>
                                    </div>
                                    <Field
                                        id="password"
                                        name="password"
                                        type="password"
                                        label=""
                                        autoComplete="current-password"
                                        component={FormInputField}
                                        validator={passwordValidator}
                                    />
                                </FormFieldSet>
                                {isError && <p className="k-form-error mb-3">Login failed. Please check your credentials.</p>}
                                {isSuccess && <p className="k-text-success mb-3">You are now signed in.</p>}
                                <Button
                                    type="submit"
                                    className="w-100 border-0 rounded-3 fw-semibold py-2 mt-2 shadow-sm"
                                    disabled={!formRenderProps.allowSubmit || isLoading}
                                    style={{ backgroundColor: "#2f8378", color: "#ffffff" }}
                                >
                                    <span className="d-inline-flex align-items-center justify-content-center gap-2">
                                        {isLoading ? "Signing In..." : "Sign In"}
                                        {!isLoading && <SvgIcon icon={arrowRightIcon} style={{ width: "16px", height: "16px" }} />}
                                    </span>
                                </Button>
                                <div className="d-flex align-items-center gap-3 my-4 text-body-secondary small">
                                    <span className="border-top flex-grow-1" />
                                    <span>or continue with</span>
                                    <span className="border-top flex-grow-1" />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <Button
                                            type="button"
                                            fillMode="outline"
                                            disabled={true}
                                            className="w-100 rounded-3 border-secondary-subtle text-body py-2"
                                            style={{ backgroundColor: "#ffffff" }}
                                        >
                                            <span className="fw-semibold">Microsoft</span>
                                        </Button>
                                    </div>

                                    <div className="col-6">
                                        <Button
                                            type="button"
                                            fillMode="outline"
                                            disabled={true}
                                            className="w-100 rounded-3 border-secondary-subtle text-body py-2"
                                            style={{ backgroundColor: "#ffffff" }}
                                        >
                                            <span className="fw-semibold">Google</span>
                                        </Button>
                                    </div>
                                </div>
                                <p className="mb-0 mt-4 text-center text-body-secondary small">
                                    Don&apos;t have an account?&nbsp;
                                    <Link className="link-success fw-semibold link-underline-opacity-0" to="/forgot-password">Request access</Link>
                                </p>
                            </FormElement>
                        )}
                    />
                </CardBody>
            </Card>
        </div>
    );
};

export default Login;
